/**
 * CC-57 — DemiBrief
 *
 * Renders a brief pushed by Claude Pro via the Hail Mary MCP connector.
 * Shape:  payload_v2.imported_raw.segments = [{name, markdown_body}]
 *
 * Markdown is user-controlled (Claude writes freeform) → sanitised with
 * rehype-sanitize. react-markdown + remark-gfm already present.
 * NO dangerouslySetInnerHTML anywhere in this file.
 */
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import rehypeSanitize, { defaultSchema } from "rehype-sanitize";
import { Sparkles } from "lucide-react";

const schema = {
  ...defaultSchema,
  attributes: {
    ...defaultSchema.attributes,
    a: [...(defaultSchema.attributes?.a || []), ["target"], ["rel"]],
  },
};

export function McpBadge({ className = "" }) {
  return (
    <span
      data-testid="demi-mcp-badge"
      className={`inline-flex items-center gap-1 rounded-md border px-1.5 py-0.5 mono uppercase tracking-widest text-[9px] ${className}`}
      style={{
        background: "var(--hm-accent-soft)",
        borderColor: "var(--hm-accent-dim)",
        color: "var(--hm-accent)",
      }}
      title="Pushed by Claude Pro via the Hail Mary MCP connector"
    >
      <Sparkles className="h-2.5 w-2.5" strokeWidth={2.5} />
      MCP
    </span>
  );
}

function Segment({ name, body }) {
  return (
    <section
      data-testid={`demi-segment-${name}`}
      className="hm-surface p-5 mb-5"
    >
      <div className="flex items-baseline gap-2 mb-3 pb-2 border-b border-hm-border">
        <h2
          className="text-hm-fg"
          style={{ fontSize: 15, fontWeight: 600, letterSpacing: "0.02em" }}
        >
          {name}
        </h2>
        <McpBadge className="ml-auto" />
      </div>
      <div className="demi-markdown text-hm-fg" data-testid={`demi-segment-body-${name}`}>
        <ReactMarkdown
          remarkPlugins={[remarkGfm]}
          rehypePlugins={[[rehypeSanitize, schema]]}
          components={{
            a: ({ node, ...props }) => (
              <a {...props} target="_blank" rel="noreferrer noopener" />
            ),
          }}
        >
          {body || ""}
        </ReactMarkdown>
      </div>
    </section>
  );
}

export default function DemiBrief({ payload }) {
  const raw = (payload && payload.imported_raw) || {};
  const segments = Array.isArray(raw.segments) ? raw.segments : [];
  const importedAt = payload?.imported_at;

  if (!segments.length) {
    return (
      <div
        className="hm-surface p-10 text-center"
        data-testid="demi-brief-empty-segments"
      >
        <Sparkles className="h-8 w-8 text-hm-fg-subtle mx-auto mb-3" />
        <div className="text-hm-fg font-medium">No segments in this brief.</div>
        <p className="text-sm text-hm-fg-muted mt-2 max-w-md mx-auto">
          Claude pushed an empty payload. Re-run <code className="mono text-xs">push_brief</code> from Claude.ai.
        </p>
      </div>
    );
  }

  return (
    <div data-testid="demi-brief-body">
      <div
        className="flex items-center gap-3 mb-4 px-4 py-3 rounded-lg"
        style={{
          background: "var(--hm-accent-soft)",
          borderWidth: 1,
          borderStyle: "solid",
          borderColor: "var(--hm-accent-dim)",
        }}
        data-testid="demi-brief-source-banner"
      >
        <Sparkles className="h-4 w-4 shrink-0" style={{ color: "var(--hm-accent)" }} />
        <div className="min-w-0 flex-1">
          <div
            className="mono uppercase tracking-widest"
            style={{ fontSize: 10, color: "var(--hm-accent-dim)" }}
          >
            Demi · pushed via MCP
          </div>
          <div className="text-hm-fg text-sm mt-0.5">
            This brief was composed by Claude Pro and pushed into Hail Mary
            through the <code className="mono text-xs">push_brief</code> MCP tool.
          </div>
          {importedAt && (
            <div className="mono text-[10px] text-hm-fg-subtle mt-1">
              imported_at · {importedAt}
            </div>
          )}
        </div>
      </div>
      {segments.map((s, i) => (
        <Segment
          key={`${s.name}-${i}`}
          name={s.name || `Segment ${i + 1}`}
          body={s.markdown_body || ""}
        />
      ))}
    </div>
  );
}
